import { useState } from "react";
import TreelLogo from "../components/atoms/TreelLogo";
import { LoginForm, RegisterForm } from "../components/organisms";
import { useLogin, useRegister } from "../hooks/auth";
import type { LoginCredentials, RegisterCredentials } from "../types/auth";

interface LoginPageProps {
  onLoginSuccess?: (user: any, token: string) => void;
}

export function LoginPage({}: LoginPageProps) {
  const [isRegisterMode, setIsRegisterMode] = useState(false);

  const loginMutation = useLogin();
  const registerMutation = useRegister();

  const handleLogin = async (credentials: LoginCredentials) => {
    loginMutation.mutate(credentials, {
      onSuccess: () => {
        // After successful login and token storage, reload the page
        // This ensures the App component re-initializes with the new auth state
        window.location.reload();
      },
    });
  };

  const handleRegister = async (credentials: RegisterCredentials) => {
    registerMutation.mutate(credentials, {
      onSuccess: () => {
        // After successful registration, automatically log the user in
        handleLogin({
          email: credentials.email,
          password: credentials.password,
          force: true,
        });
      },
    });
  };

  // Determine loading state and error from active mutation
  const isLoading = loginMutation.isPending || registerMutation.isPending;
  const error =
    loginMutation.error?.message ?? registerMutation.error?.message ?? null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-bg-primary via-bg-surface to-bg-secondary relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-pattern-subtle opacity-30" />

      {/* Floating Orbs */}
      <div className="absolute top-10 left-10 w-32 h-32 bg-gradient-to-r from-action-key-default/20 to-pressed-tomato/20 rounded-full blur-xl animate-pulse" />
      <div className="absolute top-1/4 right-16 w-24 h-24 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full blur-lg animate-pulse delay-300" />
      <div className="absolute bottom-20 left-1/4 w-40 h-40 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 rounded-full blur-2xl animate-pulse delay-700" />

      {/* Main Content */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo/Header */}
          <div className="text-center mb-8 animate-[fadeIn_0.8s_ease-out]">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-white/90 to-white/80 backdrop-blur-sm rounded-2xl shadow-2xl mb-4 border border-white/20">
              <TreelLogo size="lg" />
            </div>
            <h1 className="text-3xl font-bold text-text-primary mb-2">
              Welcome to Treel
            </h1>
            <p className="text-text-secondary text-sm">
              Your AI-powered coaching companion
            </p>
          </div>

          {/* Form Container */}
          <div className="transform transition-all duration-500 ease-out">
            {isRegisterMode ? (
              <RegisterForm
                onRegister={handleRegister}
                onSwitchToLogin={() => setIsRegisterMode(false)}
                isLoading={isLoading}
                error={error}
              />
            ) : (
              <LoginForm
                onLogin={handleLogin}
                onSwitchToRegister={() => setIsRegisterMode(true)}
                isLoading={isLoading}
                error={error}
              />
            )}
          </div>

          {/* Footer */}
          <div className="text-center mt-8 animate-[fadeIn_1.2s_ease-out]">
            <p className="text-text-muted text-xs">
              Powered by{" "}
              <strong>
                <a href="https://techery.ai">techery.ai</a>
              </strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
